import React, { Component, ComponentType } from 'react'
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, IconButton, styled, colors } from '@mui/material'
import { ISort, IFilterConfigs } from './types'
import { createFilterStore } from './filter-store'
import { ClearAllButton, mapSortConfigs } from './ui.units'
import { defaultDataViewContext, IDataViewContext, mapDataViewContext } from './context'

const sortMenuClasses = {
  root: 'SortMenu-root',
  header: 'SortMenu-header',
  list: 'SortMenu-list',
  item: 'SortMenu-item',
  activeItem: 'SortMenu-activeItem',
  itemText: 'SortMenu-itemText',
  directionButton: 'SortMenu-directionButton',
  clearButton: 'SortMenu-clearButton'
}

export interface ISortMenuProps {}

function createSortMenu<T>(): ComponentType<any> {
  class SortMenu extends Component {
    dataViewContext: IDataViewContext<T> = defaultDataViewContext
    constructor(props: ISortMenuProps) {
      super(props)
      this.dataViewContext = defaultDataViewContext
    }

    render() {
      return mapDataViewContext((context) => {
        this.dataViewContext = context
        return (
          <Wrapper className={sortMenuClasses.root}>
            <Box className={sortMenuClasses.header}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                Sort By
              </Typography>
              {this.dataViewContext.filterState.sort && (
                <ClearAllButton className={sortMenuClasses.clearButton} onClick={() => this.handleClearSort()} title='Clear sort'>
                  Clear
                </ClearAllButton>
              )}
            </Box>
            {this.renderSortList()}
          </Wrapper>
        )
      })
    }

    renderSortList = () => {
      const fields: IFilterConfigs<T> = this.dataViewContext.filterBarConfigs.fields || {}
      const fieldKeys = Object.keys(fields) as Array<keyof T>

      // Filter only sortable fields (default is true if not specified)
      const sortableFields = fieldKeys.filter((fieldKey) => {
        const fieldConfig = fields[fieldKey]
        return fieldConfig?.sortable !== false // true by default
      })

      const currentSort = this.dataViewContext.filterState.sort

      if (sortableFields.length === 0) {
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No sortable fields available
            </Typography>
          </Box>
        )
      }

      return (
        <ListStyled className={sortMenuClasses.list} disablePadding>
          {sortableFields.map((fieldKey) => {
            const fieldConfig = fields[fieldKey]
            const isActive = currentSort?.field === fieldKey
            const currentDirection = isActive ? currentSort.direction : undefined
            const sortConfig = mapSortConfigs[currentDirection || 'asc']

            return (
              <ListItem key={fieldKey.toString()} className={`${sortMenuClasses.item} ${isActive ? sortMenuClasses.activeItem : ''}`} disablePadding>
                <ListItemButton
                  onClick={() => this.handleFieldClick(fieldKey, currentDirection)}
                  className={isActive ? 'active' : ''}
                  // sx={{
                  //   backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  //   '&:hover': {
                  //     backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                  //   }
                  // }}
                >
                  <ListItemText
                    primary={fieldConfig?.label || fieldKey.toString()}
                    className={sortMenuClasses.itemText}
                    primaryTypographyProps={{ className: sortMenuClasses.itemText, sx: { fontWeight: isActive ? 600 : 400 } }}
                  />
                  {isActive && (
                    <IconButton
                      size='small'
                      className={sortMenuClasses.directionButton}
                      onClick={(e) => {
                        e.stopPropagation()
                        this.handleDirectionToggle(fieldKey, currentDirection)
                      }}
                      title={`Sort ${sortConfig.title.toLocaleLowerCase()}`}
                    >
                      {sortConfig.icon}
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
            )
          })}
        </ListStyled>
      )
    }

    handleFieldClick = (field: keyof T, currentDirection?: 'asc' | 'desc') => {
      const newDirection: 'asc' | 'desc' = currentDirection ? (currentDirection === 'asc' ? 'desc' : 'asc') : 'asc'
      const newSort: ISort<T> = { field, direction: newDirection }

      const store = createFilterStore(this.dataViewContext.filterState).changeSort(newSort, { area: 'sortButton' })

      this.dataViewContext.onFilterStateChange(store.build())
    }

    handleDirectionToggle = (field: keyof T, currentDirection?: 'asc' | 'desc') => {
      if (!currentDirection) return

      const newDirection: 'asc' | 'desc' = currentDirection === 'asc' ? 'desc' : 'asc'
      const newSort: ISort<T> = { field, direction: newDirection }

      const store = createFilterStore(this.dataViewContext.filterState).changeSort(newSort, { area: 'sortButton' })

      this.dataViewContext.onFilterStateChange(store.build())
    }

    handleClearSort = () => {
      const store = createFilterStore(this.dataViewContext.filterState).removeSort({ area: 'sortButton' })

      this.dataViewContext.onFilterStateChange(store.build())
    }
  }

  return SortMenu
}

export default createSortMenu

const Wrapper = styled(Box)(({ theme }) => ({
  minWidth: '240px',
  maxWidth: '360px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '4px',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,

  [`.${sortMenuClasses.header}`]: {
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
    padding: '8px',
    gap: '8px',
    '.MuiTypography-root': {
      color: theme.palette.common.white,
      flex: 1
    }
  },

  [`.${sortMenuClasses.list}`]: {
    maxHeight: '300px',
    overflowY: 'auto'
  },

  [`.${sortMenuClasses.item}`]: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  },

  [`.${sortMenuClasses.directionButton}`]: {
    marginLeft: 'auto',
    color: theme.palette.primary.main
  },

  [`.${sortMenuClasses.clearButton}`]: {
    color: theme.palette.error.main
  }
}))

const ListStyled = styled(List)(({ theme }) => ({
  '.MuiListItemButton-root': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
    },

    '&.active': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : 'rgba(25, 118, 210, 0.12)'
      }
    }
  }
}))
